import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const HouseKeepingOperations = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [housekeepingStatus, setHousekeepingStatus] = useState([]);
  const [stats, setStats] = useState({});
  const [todayTasks, setTodayTasks] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: '',
    cleaned_by: '',
    notes: ''
  });
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    clean: '#10b981',
    dirty: '#ef4444',
    inspected: '#3b82f6',
    outOfService: '#f59e0b'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchStatus(), fetchStats(), fetchTodayTasks()]);
    setLoading(false);
  };

  const fetchStatus = async () => {
    try {
      const data = await apiGet('/admin/housekeeping/operations/status');
      if (data.success) setHousekeepingStatus(data.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiGet('/admin/housekeeping/operations/stats');
      if (data.success) setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTodayTasks = async () => {
    try {
      const data = await apiGet('/admin/housekeeping/operations/today-tasks');
      if (data.success) setTodayTasks(data.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/housekeeping/operations/update-status/${selectedRoom.room_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Room status updated successfully!');
        setShowUpdateModal(false);
        fetchAllData();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const openUpdateModal = (room) => {
    setSelectedRoom(room);
    setUpdateData({
      status: room.status,
      cleaned_by: room.cleaned_by || '',
      notes: room.notes || ''
    });
    setShowUpdateModal(true);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      clean: colors.clean,
      dirty: colors.dirty,
      inspected: colors.inspected,
      out_of_service: colors.outOfService
    };
    return statusColors[status] || colors.textLight;
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    statValue: {
      fontSize: isMobile ? '28px' : '36px',
      fontWeight: '800',
      marginBottom: '8px'
    },
    statLabel: {
      fontSize: isMobile ? '11px' : '12px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    roomGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(4, 1fr)' : 'repeat(6, 1fr)',
      gap: isMobile ? '10px' : '12px'
    },
    roomCard: {
      padding: isMobile ? '14px' : '16px',
      borderRadius: '10px',
      border: '2px solid',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '500px',
      width: '100%'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      background: colors.white
    },
    button: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    primaryButton: {
      background: colors.primary,
      color: colors.white
    },
    successMessage: {
      padding: isMobile ? '12px 16px' : '14px 20px',
      borderRadius: '10px',
      marginBottom: '16px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      background: 'rgba(74, 124, 42, 0.15)',
      color: colors.success,
      border: `2px solid ${colors.success}`,
      margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading housekeeping data...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const roomsByFloor = housekeepingStatus.reduce((acc, room) => {
    if (!acc[room.floor]) acc[room.floor] = [];
    acc[room.floor].push(room);
    return acc;
  }, {});

  return (
    <AdminLayout>
      {successMessage && (
        <div style={styles.successMessage}>{successMessage}</div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Housekeeping Operations</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Daily tasks & cleaning' : 'Manage daily housekeeping tasks, room cleaning schedules, and maintenance'}
        </p>
      </div>

      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.clean}` }}>
          <div style={{ ...styles.statValue, color: colors.clean }}>{stats.clean_rooms || 0}</div>
          <div style={styles.statLabel}>Clean Rooms</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.dirty}` }}>
          <div style={{ ...styles.statValue, color: colors.dirty }}>{stats.dirty_rooms || 0}</div>
          <div style={styles.statLabel}>Dirty Rooms</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.inspected}` }}>
          <div style={{ ...styles.statValue, color: colors.inspected }}>{stats.inspected_rooms || 0}</div>
          <div style={styles.statLabel}>Inspected</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.outOfService}` }}>
          <div style={{ ...styles.statValue, color: colors.outOfService }}>{stats.out_of_service_rooms || 0}</div>
          <div style={styles.statLabel}>Out of Service</div>
        </div>
      </div>

      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}><span>📋</span> Today's Priority Tasks</h2>
        {todayTasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No priority tasks for today
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {todayTasks.map(task => (
              <div
                key={task.id}
                style={{
                  padding: isMobile ? '12px' : '14px',
                  background: colors.lightGray,
                  borderRadius: '8px',
                  borderLeft: `4px solid ${getStatusColor(task.status)}`,
                  cursor: 'pointer'
                }}
                onClick={() => openUpdateModal(task)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <strong>Room {task.room_number}</strong> - {task.room_type}
                    {task.guest_name && (
                      <div style={{ fontSize: '12px', color: colors.textLight }}>
                        Guest: {task.guest_name}
                        {task.check_out_date && ` • Checkout: ${new Date(task.check_out_date).toLocaleDateString()}`}
                      </div>
                    )}
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: `${getStatusColor(task.status)}20`,
                    color: getStatusColor(task.status)
                  }}>
                    {task.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {Object.keys(roomsByFloor).sort((a, b) => b - a).map(floor => (
        <div key={floor} style={styles.contentCard}>
          <h2 style={styles.sectionTitle}>
            <span>🏢</span> Floor {floor}
          </h2>
          <div style={styles.roomGrid}>
            {roomsByFloor[floor].map(room => (
              <div
                key={room.id}
                style={{
                  ...styles.roomCard,
                  borderColor: getStatusColor(room.status),
                  background: `${getStatusColor(room.status)}15`
                }}
                onClick={() => openUpdateModal(room)}
                onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1)')}
              >
                <div style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '700', marginBottom: '4px' }}>
                  {room.room_number}
                </div>
                <div style={{ fontSize: isMobile ? '10px' : '11px', color: colors.textLight, marginBottom: '6px' }}>
                  {room.room_type}
                </div>
                <div style={{
                  fontSize: isMobile ? '9px' : '10px',
                  fontWeight: '600',
                  color: getStatusColor(room.status),
                  textTransform: 'uppercase'
                }}>
                  {room.status.replace('_', ' ')}
                </div>
                {room.guest_name && (
                  <div style={{ fontSize: '9px', color: colors.textLight, marginTop: '4px' }}>
                    Occupied
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {showUpdateModal && selectedRoom && (
        <div style={styles.modal} onClick={() => setShowUpdateModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              Update Room {selectedRoom.room_number}
            </h3>

            <form onSubmit={handleUpdateStatus}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Status *</label>
                <select
                  value={updateData.status}
                  onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
                  style={styles.select}
                  required
                >
                  <option value="dirty">Dirty</option>
                  <option value="clean">Clean</option>
                  <option value="inspected">Inspected</option>
                  <option value="out_of_service">Out of Service</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Cleaned By</label>
                <input
                  type="text"
                  value={updateData.cleaned_by}
                  onChange={(e) => setUpdateData({ ...updateData, cleaned_by: e.target.value })}
                  style={styles.input}
                  placeholder="Staff name"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea
                  value={updateData.notes}
                  onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                  style={{ ...styles.input, minHeight: '80px' }}
                  placeholder="Any notes or observations..."
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...styles.button, ...styles.primaryButton, flex: 1 }}>
                  Update Status
                </button>
                <button
                  type="button"
                  onClick={() => setShowUpdateModal(false)}
                  style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default HouseKeepingOperations;